import { useRef, useState } from 'react';
import { Notepad2 } from 'iconsax-react';
import { Form, Formik, type FormikHelpers } from 'formik';

import { Button } from '@src/design-system/button';
import {
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
} from '@src/design-system/dialog';
import { type TLeadNote } from '@src/shared/types/lead-note';
import CreateNoteForm from '@shared/features/notes/create-note-form';
import { AlertTriangleIcon, LoaderCircle } from 'lucide-react';
import { FormikDatePicker } from '@src/shared/fields/formik-date-picker-field';
import { SelectField } from '@src/shared/fields/select-field';
import { Label } from '@src/design-system/label';
import leadNoteApi from '@src/features/leads/services/lead-note-api';
import {
  createLeadNoteInitialValues,
  leadNoteFormSchema,
  type TLeadNoteValues,
} from './schema';
import {
  StateBoundary,
  StateBoundaryContent,
  StateBoundaryError,
  StateBoundaryLoading,
  StateBoundaryNotFound,
} from '@src/shared/components/state-boundary';
import { LeadNoteCard } from '@src/features/leads/components/lead-note-card';
import { NOTE_CHANNELS_OPTIONS } from './helpers';
import { getLocalTimeZone, today } from '@internationalized/date';
import { useDialogContext } from '@src/design-system/dialog/use-dialog-context';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@src/design-system/tabs';
import { Badge } from '@src/design-system/badge';
import { cn } from '@src/design-system/helpers';

const NextFollowFields = () => {
  return (
    <>
      <FormikDatePicker
        name='next_follow_up_date'
        minValue={today(getLocalTimeZone())}
        label='Next Follow-up Date'
        closeOnSelect
      />

      <div>
        <Label className='mb-2'>Channel</Label>
        <SelectField
          selectName='channel'
          selectPlaceholder='Select'
          options={NOTE_CHANNELS_OPTIONS}
          renderItem={(item) => (
            <div className='flex items-center gap-2'>
              <item.icon size={20} />
              {item.label}
            </div>
          )}
        />
      </div>
    </>
  );
};

export const LeadNotesAddDialog = ({
  leadId,
  note,
}: {
  leadId: number;
  note?: TLeadNote;
}) => {
  const { formikRef } = useDialogContext();

  const { mutate: createNote, isPending: isCreating } = leadNoteApi.useCreate();

  const { mutate: updateNote, isPending: isUpdating } = leadNoteApi.useUpdate();

  const {
    data: notes,
    isLoading,
    isError,
  } = leadNoteApi.useFetch(
    {
      lead: leadId,
      page_size: 100,
    },
    { enabled: !!leadId }
  );

  const closeRef = useRef<HTMLButtonElement>(null);
  const [activeTab, setActiveTab] = useState('follow_ups');
  const [editorResetKey, setEditorResetKey] = useState(0);

  const unresolvedNotesCount =
    notes?.results?.filter((n) => !n.is_resolved && !!n.next_follow_up_date)
      .length ?? 0;

  const handleMutateNote = (
    values: TLeadNoteValues,
    helpers: FormikHelpers<TLeadNoteValues>
  ) => {
    if (note?.id) {
      updateNote(
        {
          ...values,
          id: note.id,
          attachments: values.attachments.map((file) => file.id) || [],
          is_resolved: note.is_resolved,
        },
        {
          onSuccess: () => {
            helpers.resetForm();
            closeRef.current?.click();
          },
        }
      );
    } else {
      createNote(
        {
          ...values,
          attachments: values.attachments.map((file) => file.id) || [],
          lead: leadId,
        },
        {
          onSuccess: () => {
            helpers.resetForm();
            setEditorResetKey((k) => k + 1);
          },
        }
      );
    }
  };

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={createLeadNoteInitialValues(note)}
        onSubmit={handleMutateNote}
        validationSchema={leadNoteFormSchema}
      >
        <Form className='flex flex-col h-full'>
          <DialogHeader>{note ? 'Edit Note' : 'Add Note'}</DialogHeader>
          <DialogBody className='p-0 pt-5 flex flex-col overflow-hidden'>
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className='pl-4 flex-1 flex flex-col min-h-0'
            >
              <TabsList>
                <TabsTrigger value='follow_ups'>Follow-ups</TabsTrigger>
                <TabsTrigger
                  className={cn(!!note && 'hidden')}
                  value='last_notes'
                >
                  Last Notes{' '}
                  {unresolvedNotesCount > 0 && (
                    <Badge
                      className={cn(
                        'ml-2 text-xs font-medium w-5 h-5 flex items-center justify-center',
                        activeTab === 'last_notes'
                          ? 'bg-[#ECDFFB] text-primary-1000'
                          : 'bg-gray-50 text-gray-600'
                      )}
                      variant='secondary'
                    >
                      {unresolvedNotesCount}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              <TabsContent
                value='follow_ups'
                className='m-0 scrollbar-gray flex-1 flex flex-col min-h-0'
              >
                <div className='flex-1 min-h-0 overflow-y-auto'>
                  <CreateNoteForm key={editorResetKey} />

                  <div className='grid grid-cols-2 gap-4 mb-6'>
                    <NextFollowFields />
                  </div>
                </div>

                <DialogFooter className='p-0 pb-4 pr-4 pt-10 shrink-0'>
                  <Button
                    variant='primary'
                    className='w-full rounded-xl'
                    type='submit'
                    isLoading={isCreating || isUpdating}
                  >
                    Confirm
                  </Button>
                  <DialogClose
                    asChild
                    ref={closeRef}
                  >
                    <Button
                      variant='primaryPale'
                      className='bg-transparent w-full rounded-xl font-medium text-primary-1000 border-primary-1000'
                    >
                      Cancel
                    </Button>
                  </DialogClose>
                </DialogFooter>
              </TabsContent>
              <TabsContent
                value='last_notes'
                className='scrollbar-gray pt-4 flex-1 flex flex-col min-h-0'
              >
                <div className='flex-1 min-h-0 overflow-y-auto'>
                  <StateBoundary
                    isLoading={isLoading}
                    isError={isError}
                    isNotFound={!notes?.count}
                  >
                    <StateBoundaryLoading className='flex justify-center my-4'>
                      <LoaderCircle
                        className='animate-spin'
                        size={30}
                      />
                    </StateBoundaryLoading>
                    <StateBoundaryNotFound className='flex flex-col justify-center items-center gap-3 text-sm text-gray-500 mt-10'>
                      <Notepad2 size={32} />
                      <span>No notes available</span>
                    </StateBoundaryNotFound>
                    <StateBoundaryError className='flex text-destructive flex-col justify-center items-center gap-3 text-sm mt-10'>
                      <AlertTriangleIcon size={32} />
                      <span>An error occurred while fetching notes</span>
                    </StateBoundaryError>
                    <StateBoundaryContent>
                      {notes?.results.map((note) => (
                        <LeadNoteCard
                          key={note.id}
                          note={note}
                        />
                      ))}
                    </StateBoundaryContent>
                  </StateBoundary>
                </div>
              </TabsContent>
            </Tabs>
          </DialogBody>
        </Form>
      </Formik>
    </>
  );
};
